import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { inferOutput } from '@trpc/tanstack-react-query'
import { Alert, Button, Form, Input, Radio, Skeleton, Tag, Typography } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import dayjs from 'dayjs'
import { RefreshCw, Send } from 'lucide-react'
import { wecomTestSendSchema, type WecomTestSendInput } from '@super-mega/server/contracts/wecom-push'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { wecomDeliveryStatus, wecomMessageTypeOptions } from './const/wecom-push.ts'

const fieldRule = createSchemaFieldRule(wecomTestSendSchema.pick({ chatId: true, messageType: true }))
type TestForm = Pick<WecomTestSendInput, 'chatId' | 'messageType'>
type Delivery = inferOutput<ReturnType<typeof useTRPC>['bot']['wecomDelivery']>

export function BotWecomPush({ botId }: { botId: string }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const access = useAccess()
  const connection = useQuery({ ...trpc.bot.wecomConnection.queryOptions({ botId }), refetchInterval: 10_000 })
  const send = useMutation(
    trpc.bot.testWecomPush.mutationOptions({
      retry: false,
      onSuccess: () =>
        Promise.all([
          cache.invalidateQueries(trpc.bot.wecomConnection.queryFilter({ botId })),
          cache.invalidateQueries(trpc.bot.wecomDeliveries.queryFilter({ botId }))
        ])
    })
  )
  const deliveryId = send.data?.id ?? connection.data?.latestDeliveryId
  const delivery = useQuery({
    ...trpc.bot.wecomDelivery.queryOptions({ id: deliveryId ?? 0 }),
    enabled: Boolean(deliveryId),
    refetchInterval: (query) => {
      if (query.state.data && ['pending', 'sending'].includes(query.state.data.status)) return 2000
      return false
    }
  })
  const currentDelivery = delivery.data
  const status = currentDelivery?.status
  const isSending = send.isPending || status === 'pending' || status === 'sending'
  const connected = Boolean(connection.data?.connected)
  const error = connection.error ?? send.error ?? delivery.error

  return (
    <div className="space-y-5">
      <ConnectionStatus
        connected={connected}
        pending={connection.isPending}
        fetching={connection.isFetching}
        lastSeenAt={connection.data?.lastSeenAt}
        onRefresh={() => void connection.refetch()}
      />
      {error && <Alert type="error" showIcon title={error.message} />}
      {!connection.isPending && !connection.error && !connected && (
        <Alert
          className="mb-3!"
          type="info"
          showIcon
          title="当前后台尚未收到有效的企微推送网关心跳"
          description="测试消息由 Hermes 客服网关发送。请确认正在运行的网关已加载最新推送插件，并连接当前后台；部署插件后重启 customer-service。"
        />
      )}
      {access.can('bot.push.test') && (
        <PushTestForm
          connected={connected}
          isSending={isSending}
          onSubmit={(values) => send.mutate({ ...values, botId, requestId: crypto.randomUUID() })}
        />
      )}
      {currentDelivery && <DeliveryResult delivery={currentDelivery} />}
    </div>
  )
}

function ConnectionStatus({
  connected,
  pending,
  fetching,
  lastSeenAt,
  onRefresh
}: {
  connected: boolean
  pending: boolean
  fetching: boolean
  lastSeenAt?: Date | null
  onRefresh: () => void
}) {
  return (
    <section className="bg-surface border-border rounded-xl border p-5 sm:p-6" aria-label="企微连接状态">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="m-0 text-base font-semibold">企微主动推送</h2>
          <Tag color={connected ? 'success' : 'default'}>{connected ? '通道就绪' : '通道未就绪'}</Tag>
        </div>
        <Button icon={<RefreshCw className="size-4" />} loading={fetching} onClick={onRefresh}>
          刷新状态
        </Button>
      </div>
      <p className="text-content-muted mt-3 mb-0 text-sm leading-6">
        客服对话和后台推送共用当前机器人，测试消息只包含联调说明。
      </p>
      {pending && <Skeleton active paragraph={{ rows: 1 }} />}
      {lastSeenAt && (
        <p className="text-content-muted mt-2 mb-0 text-xs">
          最近心跳：{dayjs(lastSeenAt).format('YYYY-MM-DD HH:mm:ss')}
        </p>
      )}
    </section>
  )
}

function PushTestForm({
  connected,
  isSending,
  onSubmit
}: {
  connected: boolean
  isSending: boolean
  onSubmit: (values: TestForm) => void
}) {
  return (
    <section className="bg-surface border-border overflow-hidden rounded-xl border">
      <div className="border-border border-b px-5 py-4 sm:px-6">
        <h2 className="m-0 text-base font-semibold">发送联调消息</h2>
        <p className="text-content-muted mt-1 mb-0 text-sm">
          将机器人加入测试群，填写该群的 chatid，分别验证文字和 PDF。
        </p>
      </div>
      <Form<TestForm>
        layout="vertical"
        className="p-5! sm:p-6!"
        initialValues={{ messageType: 'markdown' }}
        disabled={isSending}
        scrollToFirstError={{ focus: true }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="chatId"
          label="测试企微群 ID"
          required
          rules={[fieldRule]}
          extra="填写企微返回的群聊 chatid；此处仅发送固定联调内容。"
        >
          <Input placeholder="wr_…" autoComplete="off" maxLength={255} spellCheck={false} />
        </Form.Item>
        <Form.Item name="messageType" label="消息类型" required rules={[fieldRule]}>
          <Radio.Group options={wecomMessageTypeOptions} optionType="button" />
        </Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          icon={<Send className="size-4" />}
          loading={isSending}
          disabled={!connected}
        >
          发送测试消息
        </Button>
      </Form>
    </section>
  )
}

function DeliveryResult({ delivery }: { delivery: Delivery }) {
  const feedback = wecomDeliveryStatus[delivery.status]
  if (!feedback) return null
  return (
    <Alert
      type={feedback.type}
      showIcon
      title={feedback.title}
      description={
        <div className="space-y-2">
          <p className="m-0">{feedback.description}</p>
          <p className="m-0 text-xs">
            记录 #{delivery.id} · {delivery.messageType === 'pdf' ? 'PDF' : '文字'} ·{' '}
            {dayjs(delivery.createdAt).format('YYYY-MM-DD HH:mm:ss')}
          </p>
          <Typography.Text copyable className="break-all">
            {delivery.chatId}
          </Typography.Text>
          {delivery.error && <p className="m-0">{delivery.error}</p>}
        </div>
      }
    />
  )
}
